import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import Seo from "@/lib/Seo";
import { requestDownloadLink } from "@/lib/api";
import { API } from "@/lib/adminApi";
import { track } from "@/analytics/metaPixel";

const TYPE_LABEL = { article: "مقالة", release: "إصدار", "case-study": "دراسة حالة", research: "بحث" };
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ar-SA", { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; }
}

function DownloadGate({ slug, title }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      await requestDownloadLink(email, slug);
      // Meta Pixel: count a Lead for THIS publication only after server success
      track("Lead", {
        content_name: title,
        content_ids: [slug],
        content_type: "article",
      });
      setState("done");
    }
    catch { setState("error"); }
  };
  return (
    <div className="mt-12 bg-navy text-white p-8" style={{ backgroundColor: "var(--navy)" }}>
      <h2 className="font-display text-2xl">حمّل من هنا</h2>
      <p className="mt-2 text-white/70 leading-relaxed">أدخل بريدك ليصلك رابط التحميل.</p>
      {state !== "done" ? (
        <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="download-form">
          <label htmlFor="dl-email" className="sr-only">البريد الإلكتروني</label>
          <input id="dl-email" type="email" required dir="ltr" value={email} onChange={(e) => { setEmail(e.target.value); setState("idle"); }}
            placeholder="you@example.com" className="flex-1 bg-white/5 border border-white/20 px-4 py-3 text-white placeholder:text-white/40 outline-none text-right" data-testid="download-email" />
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" onChange={() => {}} />
          <button type="submit" disabled={state === "loading"} className="inline-flex items-center justify-center gap-2 bg-coral px-6 py-3 font-semibold" style={{ backgroundColor: "var(--coral)" }} data-testid="download-submit">
            {state === "loading" ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} احصل على الرابط
          </button>
        </form>
      ) : (
        <p className="mt-5 flex items-center gap-2 text-peach" role="status" data-testid="download-success"><Download size={18} /> تم استلام طلبك، وستجد رابط التحميل أيضًا على بريدك الإلكتروني المسجل.</p>
      )}
      {state === "error" && <p className="mt-3 text-sm" style={{ color: "var(--coral)" }} role="alert">تعذّرت العملية، حاول مجددًا.</p>}
    </div>
  );
}

export default function KnowledgeDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    let ignore = false;
    fetch(`${API}/publications/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => !ignore && setItem(data))
      .catch(() => !ignore && setItem(null));
    return () => { ignore = true; };
  }, [slug]);

  // Meta Pixel: each publication view reported separately by its slug
  useEffect(() => {
    if (!item) return;
    track("ViewContent", {
      content_name: item.title,
      content_ids: [item.slug],
      content_type: "article",
    });
  }, [item]);


  if (item === undefined) {
    return <div className="min-h-[60vh] grid place-items-center text-blue"><Loader2 className="animate-spin" /></div>;
  }
  if (item === null) return <Navigate to="/publications" replace />;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: item.title, inLanguage: "ar", datePublished: item.published_at,
  };

  return (
    <>
      <Seo path={`/knowledge/${item.slug}`} title={item.title} description={item.meta_description || item.excerpt} type="article" jsonLd={jsonLd} />
      <article className="bg-navy text-white pt-32 pb-16" style={{ backgroundColor: "var(--navy)" }}>
        <div className="mx-auto max-w-[900px] px-6 sm:px-10">
          <nav aria-label="مسار التنقل" className="text-sm text-white/60 mb-6">
            <Link to="/publications" className="hover:text-white">إصداراتنا المعرفية</Link> <span className="mx-2">/</span> <span className="text-white/80">{item.title}</span>
          </nav>
          <span className="text-sm font-semibold" style={{ color: "var(--peach)" }}>{TYPE_LABEL[item.type] || "إصدار"}</span>
          <h1 className="mt-3 font-display leading-tight" style={{ fontSize: "clamp(1.6rem, 3vw, 2.5rem)" }}>{item.title}</h1>
          <p className="mt-4 text-white/60 text-sm">{fmtDate(item.published_at)}</p>
        </div>
      </article>
      <div className="bg-paper py-16">
        <div className="mx-auto max-w-[900px] px-6 sm:px-10">
          {item.cover_image && (
            <img
              src={item.cover_image}
              alt={item.title}
              loading="lazy"
              decoding="async"
              data-testid="publication-cover"
              className="w-full h-auto max-h-[520px] object-contain mb-8 bg-navy/5"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="text-blue text-lg leading-loose whitespace-pre-wrap">{item.body}</div>
          {item.pdf_url && (
            <a href={item.pdf_url} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 bg-coral text-white px-6 py-3">
              <Download size={16} /> تحميل الملف {item.pdf_filename ? `(${item.pdf_filename})` : ""}
            </a>
          )}
          {item.pdf_url && <DownloadGate slug={item.slug} title={item.title} />}
          <Link to="/publications" className="mt-10 inline-flex items-center gap-2 text-navy font-medium link-underline">
            <ArrowLeft size={16} /> العودة إلى الإصدارات المعرفية
          </Link>
        </div>
      </div>
    </>
  );
}
